import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { User } from '../my-type';
import { State } from '../state';
import { hasAdminSiteAccess } from '../util/roles';
import { updateUser as updateUserAction } from '../state/action-creators';

interface UseAuthStatusOptions {
  redirectToLogin?: boolean;
  redirectPath?: string;
}

const useAuthStatus = (options: UseAuthStatusOptions = {}) => {
  const { redirectToLogin = false, redirectPath = '/login' } = options;
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const user: User | null = useSelector((state: State) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const checkAuthStatus = useCallback(async (user: User | null) => {
    try {
      if (user && !hasAdminSiteAccess(user.roleString)) {
        dispatch(updateUserAction(null) as any);
        setLoggedIn(false);
        return;
      }

      if (user?.lineAccessToken) {
        if (new Date() > new Date(user.tokenExpiry || '1970-01-01')) {
          console.log('Stored user token expired');
          setLoggedIn(false);
          return;
        }
        // console.log('Stored user found, checking LINE accesstoken validity');
        // const isLineTokenValid = await checkLINEAccessTokenValidity(user.lineAccessToken);
        // setLoggedIn(isLineTokenValid);
        // console.log(isLineTokenValid ? 'LINE accesstoken valid' : 'LINE accesstoken expired');
        setLoggedIn(true);
      } else if (user?.jwtToken) {
        setLoggedIn(true);
      } else {
        console.log('No stored user or lineAccessToken');
        setLoggedIn(false);
      }
    } catch (error) {
      console.error('Failed to check auth status', error);
      setLoggedIn(false);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    setLoading(true); // Reset loading state on user change
    checkAuthStatus(user);
  }, [checkAuthStatus, user]);

  // Handle redirect logic when authentication fails
  useEffect(() => {
    if (!loading && !loggedIn && redirectToLogin) {
      const currentLocation = window.location.pathname;
      window.location.href = `${redirectPath}?location=${currentLocation}`;
    }
  }, [loading, loggedIn, redirectToLogin, redirectPath, navigate]);

  const authStatus = useMemo(() => ({ loggedIn, loading, user }), [loggedIn, loading, user]);
  return authStatus;
};

export default useAuthStatus;
